import React, { useEffect, useRef } from 'react';
import AuthRoute from './AuthRoute';

import { Route, Switch, withRouter, RouteComponentProps, Redirect, useLocation } from 'react-router-dom';
import { StartMixpanelLogging } from '@ygtechnologies/bygr-frontend-tools';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';

import LoginRegister from '@/components/application-user/LoginRegister';
import Logout from '@/components/application-user/Logout';
import ForgotPassword from '@/components/application-user/ForgotPassword';
import ResetPassword from '@/components/application-user/ResetPassword';
import ConfirmEmail from '@/components/application-user/ConfirmEmail';
import ProfileDetails from '@/components/app/ProfileRouting';
import InvitationDetails from '@/components/application-user/InvitationDetails';
import NotFound from '@/components/error-pages/NotFound';
import ProfileSwitcher from './ProfileSwitcher';
import { getRelativePath } from '@/utils/url';
import { ImpersonateBuyer, EndBuyerImpersonation } from './BuyerImpersonation';
import ExternalLinksRouting from './ExternalLinkRouting';
import ExternalUserRequest from '@/components/application-user/ExternalUser/ExternalUserRequest';
import ExternalUserValidate from '@/components/application-user/ExternalUser/ExternalUserValidate';
import { ConfirmMeeting } from '@/components/application-user/ConfirmMeeting';
import { ShowroomPage } from '@/showroom/ShowroomPage';
import { RootStyle, useThemeStyle, rucoriaTheme, rucoriaDarkTheme } from '@ygtechnologies/rubus-ui';
import { useCurrentCulture, useIsSeller } from '@/utils/hooks';
import { isProd } from '@/utils/envRenderLogic';
import { useThemePreference } from './ThemePreferenceContext';
import { SellerLegacyStyleProvider } from './SellerLegacyStyleProvider';

type MainRoutingProps = RouteComponentProps<any> & {
    isLoggedIn: boolean;
};

const MainRouting: React.FC<MainRoutingProps> = ({ match, location, isLoggedIn }) => {
    const baseUrl = match ? match.url : '/';

    const user = useSelector((state: IState) => state.global.user);
    const [cultureInfo] = useCurrentCulture();

    const project = useSelector((state: IState) => state.global.activeProject);
    const profile = useSelector((state: IState) => state.global.activeProfile);

    const { themePreference } = useThemePreference();
    const isSeller = useIsSeller();
    const darkModeAllowed = isSeller && !isProd;
    const theme = darkModeAllowed && themePreference === 'dark' ? rucoriaDarkTheme : rucoriaTheme;
    const { styles } = useThemeStyle(theme, { emitLegacyAliases: true });

    return (
        <>
            <RootStyle value={styles} />
            <SellerLegacyStyleProvider />

            <StartMixpanelLogging
                useNavigate={useLocation as any}
                user={{
                    id: user && user.uniqueId ? user.uniqueId : 'unknown',
                    userType: ((user, profile) => {
                        if (user && user.email && user.email.indexOf('bygr.io') > -1) {
                            return 'Internal';
                        } else if (profile) {
                            return profile.type;
                        }
                        return 'unknown';
                    })(user, profile),
                }}
                project={{
                    id: project ? project.id : 0,
                    name: project ? project.name : 'unknown',
                }}
                company={{
                    id: project ? project.sellerProfileId : 0,
                    name: project ? project.sellerProfileName : 'unknown',
                }}
            />
            <Switch>
                <AuthRoute exact path={`${baseUrl}login`} component={LoginRegister} />
                <AuthRoute exact path={`${baseUrl}logout`} component={Logout} />
                <AuthRoute exact path={`${baseUrl}external-user/requested`} component={ExternalUserRequest} />
                <AuthRoute exact path={`${baseUrl}external-user/validate`} component={ExternalUserValidate} />
                {/* <AuthRoute exact path={`${baseUrl}register/:type?/:step?`} component={LoginRegister} /> */}
                <AuthRoute exact path={`${baseUrl}forgot-password`} component={ForgotPassword} />
                <AuthRoute exact path={`${baseUrl}reset-password`} component={ResetPassword} />
                <AuthRoute exact path={`${baseUrl}confirm-email`} component={ConfirmEmail} />
                <AuthRoute exact path={`${baseUrl}invitation`} component={InvitationDetails} />

                <AuthRoute exact path={`${baseUrl}impersonate/end`} component={EndBuyerImpersonation} />
                <AuthRoute exact path={`${baseUrl}impersonate/:residentialUnitId`} component={ImpersonateBuyer} />

                <AuthRoute exact path={`${baseUrl}ext/meeting/confirm`} component={ConfirmMeeting} />

                {/* Public, anonymous showroom — must be matched before the auth redirect below. */}
                <Route path={`${baseUrl}showroom/:projectSlug/:unitId/:hash`} component={ShowroomPage} />

                {!isLoggedIn && (
                    <Redirect
                        to={`/login?returnUrl=${encodeURIComponent(getRelativePath(location)) + `&hs_lang=${cultureInfo?.languageCode || ''}`}`}
                    />
                )}

                {isLoggedIn && [
                    // <AuthRoute exact path={`${baseUrl}settings`} component={UserSettings} key="1" />,
                    <AuthRoute path={`${baseUrl}profiles/:profileId/:returnUrl*`} component={ProfileSwitcher} key="1" />,
                    <AuthRoute path={`${baseUrl}ext`} component={ExternalLinksRouting} key="2" />,
                    <AuthRoute path={`${baseUrl}`} component={ProfileDetails} key="3" />,
                ]}

                <Route component={NotFound} />
            </Switch>
        </>
    );
};

export default withRouter(MainRouting);
